/** @jsx jsx */
import React, { memo, useMemo, type ComponentPropsWithoutRef } from 'react';
import { styled, css, jsx } from '@compiled/react';
import Button from '@atlaskit/button';
import Icon from '@atlaskit/icon';
import PinIcon from '@atlaskit/icon/core/pin';
import PinFilledIcon from '@atlaskit/icon/core/pin-filled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import {
	useProjectKey,
	useProjectType,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { getEdition, PREMIUM_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { getApplicationForProject } from '@atlassian/jira-shared-types/src/application.tsx';
import PinFilled from './assets/pin-filled.tsx';
import PinUnfilled from './assets/pin-unfilled.tsx';
import messages from './messages.tsx';
import {
	fieldPinButtonSelectorName,
	fieldPinWithButtonSelectorName,
	FIELD_PIN_BUTTON_COMPONENT_SELECTOR,
	unpinnedIconWrapperSelectorName,
	UNPINNED_ICON_WRAPPER_COMPONENT_SELECTOR,
} from './utils.tsx';

export type FieldPinInnerProps = {
	isPinned?: boolean;
	label?: string;
	hasPinnedFields?: boolean;
	onClick: () => void;
};

const PIN_FIELD_ID = 'issue-field-pin.field-pin';

export const FieldPinInner = memo<FieldPinInnerProps>((props: FieldPinInnerProps) => {
	const { isPinned = false, onClick, hasPinnedFields, label } = props;
	const { formatMessage } = useIntl();

	const tooltipMessage = useMemo((): string => {
		if (isPinned) {
			return formatMessage(messages.unpinField);
		}

		if (hasPinnedFields === undefined || hasPinnedFields) {
			// This renders typical (messages.pinField) 'Pin to top' message
			return formatMessage(messages.pinField);
		}

		return formatMessage(messages.firstPinnedField);
	}, [formatMessage, hasPinnedFields, isPinned]);

	const iconOld = isPinned ? PinFilled : PinUnfilled;
	const IconWrapper = isPinned ? PinnedIconWrapper : UnpinnedIconWrapper;

	const icon = isPinned ? (
		<PinFilledIcon
			testId="issue-field-pin.pin-filled-icon"
			color={token('color.icon.accent.yellow')}
			label=""
			LEGACY_size="small"
		/>
	) : (
		<PinIcon
			testId="issue-field-pin.pin-icon"
			label=""
			LEGACY_size="small"
			color={token('color.icon')}
		/>
	);

	const ariaLabel = label ? `${label} ${tooltipMessage}` : tooltipMessage;
	return (
		<FieldPinButton data-testid={PIN_FIELD_ID}>
			<Tooltip content={tooltipMessage}>
				<FieldPinWithButtonWrapper
					onClick={onClick}
					aria-pressed={isPinned}
					aria-label={ariaLabel}
					iconBefore={
						isVisualRefreshEnabled() ? (
							icon
						) : (
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							<IconWrapper className="issue-details-pin-icon-wrapper">
								{/* @ts-expect-error - TS2322 - Type '(props: { [key: string]: string; }) => JSX.Element' is not assignable to type 'ComponentType<CustomGlyphProps> | undefined'. */}
								<Icon glyph={iconOld} />
							</IconWrapper>
						)
					}
				/>
			</Tooltip>
		</FieldPinButton>
	);
});
FieldPinInner.displayName = 'FieldPin';

export default FieldPinInner;

const FieldPinWithButton = (props: ComponentPropsWithoutRef<typeof Button>) => (
	<Button
		data-component-selector={fieldPinWithButtonSelectorName}
		spacing="none"
		testId="issue-field-pin.button"
		appearance="subtle"
		{...props}
		interactionName="auto-generated.issue-field-pin.button.button"
	/>
);

const FieldPinButton = (props: ComponentPropsWithoutRef<typeof FieldPinButtonComponent>) => (
	<FieldPinButtonComponent data-component-selector={fieldPinButtonSelectorName} {...props} />
);

const PinnedIconWrapper = (props: ComponentPropsWithoutRef<typeof PinnedIconWrapperComponent>) => (
	<PinnedIconWrapperComponent data-component-selector={fieldPinButtonSelectorName} {...props} />
);

const UnpinnedIconWrapper = (
	props: ComponentPropsWithoutRef<typeof UnpinnedIconWrapperComponent>,
) => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectType = useProjectType(projectKey) || null;
	const isJSMProject = projectType === SERVICE_DESK_PROJECT;
	const appEditions = useAppEditions();
	let editionFromTenantContext;
	if (projectType) {
		editionFromTenantContext = getEdition(getApplicationForProject(projectType), appEditions);
	}

	return !isJSMProject &&
		editionFromTenantContext !== PREMIUM_EDITION &&
		(expVal('pin_fields_observation_exp', 'isPinFieldObservabilityExpEnabled', false) ||
			expVal(
				'jira_pin_field_configurability_experiment',
				'isPinFieldConfigurabilityEnabled',
				false,
			)) ? (
		<div
			css={UnpinnedIconWrapperComponentForPinFieldConfigurability}
			data-component-selector={unpinnedIconWrapperSelectorName}
			{...props}
		/>
	) : (
		<UnpinnedIconWrapperComponent
			data-component-selector={unpinnedIconWrapperSelectorName}
			{...props}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldPinWithButtonWrapper = styled(FieldPinWithButton)({
	background: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':focus, :hover': {
		background: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`:focus ${FIELD_PIN_BUTTON_COMPONENT_SELECTOR}`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.icon.accent.yellow'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`:focus ${UNPINNED_ICON_WRAPPER_COMPONENT_SELECTOR}`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.icon'),
	},
});

/**
 * @deprecated `FieldPinButtonControl` is used as component selector in `fieldHeadingIconsCss`
 * which is consumed by other styled-components (e.g. src/packages/servicedesk/post-incident-review/src/ui/primary-incident/styled.tsx )
 * TODO: Remove `FieldPinButtonControl` once its consumers are migrated to CompiledCSS
 */
// TODO: migrate to object syntax. Autofix is available for many cases. Remove the eslint-disable for @atlaskit/design-system/no-styled-tagged-template-expression to check.
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const FieldPinButtonControl = styled.div({
	cursor: 'pointer',
	marginLeft: token('space.050'),
	flexShrink: '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-component-selector="jira-issue-field-pin-with-button-wrapper"]': {
		'&:focus, &:hover': {
			background: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:focus [data-component-selector="jira-issue-field-pin-field-pin-button"]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			color: token('color.icon.accent.yellow'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:focus [data-component-selector="jira-issue-field-pin-unpinned-icon-wrapper"]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			color: token('color.icon'),
		},
		background: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldPinButtonComponent = styled.div({
	cursor: 'pointer',
	marginLeft: token('space.050'),
	flexShrink: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PinnedIconWrapperComponent = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.icon.accent.yellow'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.icon.accent.yellow'),
	},
});

const UnpinnedIconWrapperComponentForPinFieldConfigurability = css({
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.icon'),
	},
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnpinnedIconWrapperComponent = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.inverse'),
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.icon'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export { FieldPinButtonControl, PinnedIconWrapper };
